import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Col, Input, Modal, ModalBody, ModalHeader, Row } from "reactstrap";

import { useOrganisationDetail } from "../../hooks/useOrganisation";
import { useGetActivePolicyTags } from "../../hooks/company-policy/useCompanyPolicyTags";
import { CompanyPolicyTag } from "../../interfaces/models/CompanyPolicyTag";
import { useGetPolicyNudges } from "../../hooks/company-policy/useCompanyPolicyNudges";
import { CompanyPolicyNudge } from "../../interfaces/models/CompanyPolicyNudge";

import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { Breadcrumb, CompanyPolicyStatus, SelectOption } from "../../helper/constants";
import { Organisation } from "../../interfaces/models/Organisation";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { CompanyPolicy } from "../../interfaces/models/CompanyPolicy";
import { useGetPolicyList } from "../../hooks/company-policy/useCompanyPolicies";
import RichFormSelect from "../../components/Shared/Forms/RichFormSelect";
import CompanyPolicyAndNudgeHistoryModal from "../../components/Compliance/CompanyPolicyAndNudgeHistoryModal";
import CompanyPolicyTable from "../../components/Compliance/CompanyPolicyTable";
import CompanyPolicyNudgeTable from "../../components/Compliance/CompanyPolicyNudgeTable";

interface RouteParams {
  id: string;
}

interface FilterParams {
  search: string,
  status: SelectOption;
  tag: SelectOption
}

const OrganisationCompliancePoliciesPage = () => {
  const { id } = useParams<RouteParams>(); // Organisation ID

  const StatusFilterOptions: SelectOption[] = [
    { label: 'Show Active Policies', value: CompanyPolicyStatus.ACTIVE.toString() },
    { label: 'Show Archived Policies', value: CompanyPolicyStatus.ARCHIVED.toString() }
  ]

  const [breadcrumbsData, setBreadcrumbsData] = useState<Breadcrumb[]>([
    { name: "Content List", path: `${DASHBOARD_ROOT}/content-management/` },
    { name: "Organisation List", path: `${DASHBOARD_ROOT}/content-management/organisation-list` },
    { name: '...', path: `${DASHBOARD_ROOT}/content-management/organisation-list/${id}` },
    { name: 'Policy Hub', path: `${DASHBOARD_ROOT}/content-management/organisation-list/${id}/policies` },
  ]);

  const [selectedPolicyId, setSelectedPolicyId] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const organisationResult = useOrganisationDetail(id);
  const { data, loading, error } = useGetPolicyList(id);
  const tagResult = useGetActivePolicyTags(id);
  const nudgesResult = useGetPolicyNudges(selectedPolicyId, id);
  

  const [policies, setPolicies] = useState<CompanyPolicy[]>([]);
  const [nudges, setNudges] = useState<CompanyPolicyNudge[]>([]);
  const [tags, setTags] = useState<SelectOption[]>([]);

  const [filter, setFilter] = useState<FilterParams>({
    search: "",
    status: StatusFilterOptions[0],
    tag: null
  });

  const [selectedPolicyHistory, setSelectedPolicyHistory] = useState<CompanyPolicy>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);

  const [isNudgeModalOpen, setIsNudgeModalOpen] = useState<boolean>(false);
  const [selectedNudgeHistory, setSelectedNudgeHistory] = useState<CompanyPolicyNudge>(null);
  const [isReloading, setIsReloading] = useState(false);

  const results = useMemo(() => {
    const status = Number(filter.status.value);
    const tag = filter.tag;
    if (!filter.search) return policies.filter((item) => item.status === status && (tag ? item.tags.includes(tag.value): true) );
    const search = filter?.search?.toLowerCase();
    return policies.filter((item) =>
      item.name.toLowerCase().includes(search) && item.status === status && (tag ? item.tags.includes(tag.value): true)
    );
  }, [filter, policies]);

  useEffect(() => {
    if(organisationResult.data) {
      const raw = organisationResult.data.organisation as Organisation;

      setBreadcrumbsData(prevData => {
        const updatedData = [...prevData];
        updatedData[updatedData.length - 2] = {
          ...updatedData[updatedData.length - 2], 
          name: raw.name
        };
        return updatedData;
      });
    }
  },[organisationResult.data]);

  useEffect(() => {
    if (data) {
      setPolicies(data.company_policies);
    }
  }, [data]);

  useEffect(() => {
    if(tagResult.data) {
      const rawTags = tagResult.data.company_policy_tags as CompanyPolicyTag[];
      setTags(rawTags.map((u) => ({
        label: u.name,
        value: u.name
      })));
    }
  },[tagResult.data]);

  useEffect(() => {
    if (nudgesResult.data) {
      const nudgesRaw = nudgesResult.data.company_policy_nudges;
      setNudges(nudgesRaw);
    }
  }, [nudgesResult.data]);
  
  //#region History
  const handleOpenHistory = (item: CompanyPolicy) => {
    setIsHistoryModalOpen(true); 
    setSelectedPolicyHistory(item);
  }
  //#endregion

  //#region View Nudges
  const handleViewNudges = (item: CompanyPolicy) => {
    setSelectedPolicyId(item._id);
    setSelectedPolicy(item);
    setIsNudgeModalOpen(true);
    setIsReloading(true);
    nudgesResult.refetch({
      filter: {
        policy_id: item._id,
        organisation_id: id,
      }
    });
    setIsReloading(false);
  }

  const handleOpenNudgeHistory = (item: CompanyPolicyNudge) => {
    setIsHistoryModalOpen(true);
    setSelectedNudgeHistory(item);
  }
  //#endregion
  
  return(
    <>
      <Breadcrumbs crumbs={breadcrumbsData} />
      {error && <Alert color="danger">Oops, we’re having trouble getting the Policy List. Please give it a minute, and if it doesn’t resolve, let us know.</Alert>}
      <Row className="mb-3">
        <Col md={4} xs={12} className="mt-1 pr-0">
            <Input 
              className="w-100" 
              type="text" 
              placeholder="Search Policy names here" 
              value={filter.search} 
              onChange={(e) => setFilter((prev) => ({...prev, search: e.target.value || ""}))} 
            />
        </Col>
        <Col md={4} xs={12} className="mt-1 h-100 pl-1 pr-0" >
          <RichFormSelect
            isClearable
            value={filter.tag}
            options={tags}
            onChange={async (e) => {
              setFilter((prev) => ({...prev, tag: e}))
            }}
            placeholder="Filter by Tag"
          />
        </Col>
        <Col md={4} xs={12} className="mt-1 h-100 pl-1 pr-0" >
          <RichFormSelect
            value={filter.status}
            options={StatusFilterOptions}
            isSearchable={false}
            onChange={async (e) => {
              setFilter((prev) => ({...prev, status: e}))
            }}
          />
        </Col>
      </Row>

      <CompanyPolicyTable 
        data={results}
        loading={loading}
        hasCRUD={false}
        handleOpenHistory={handleOpenHistory}
        handleViewNudges={handleViewNudges}
      />

      <Modal isOpen={isNudgeModalOpen}  size="xl">
        <ModalHeader toggle={() => setIsNudgeModalOpen((prev) => !prev)} tag="h3" className="mb-0">
          Policy Nudges
        </ModalHeader>
        <ModalBody>
          {
            selectedPolicy && 
            <CompanyPolicyNudgeTable 
              loading={isReloading}
              isOnProcess={false}
              nudges={nudges}
              hasCRUD={false}
              policy={selectedPolicy}
              handleOpenNudgeHistory={handleOpenNudgeHistory}
            />
          }
        </ModalBody>
      </Modal>

      {(selectedPolicyHistory?._id || selectedNudgeHistory?._id)&& 
      <CompanyPolicyAndNudgeHistoryModal
        isOpen={isHistoryModalOpen}
        toggle={() =>{ 
          setIsHistoryModalOpen((prev) => !prev); 
          setSelectedPolicyHistory(null);
          setSelectedNudgeHistory(null);
        }}
        policyId={selectedPolicyHistory?._id}
        nudgeId={selectedNudgeHistory?._id}
      />}        
    </>
  )

}


export { OrganisationCompliancePoliciesPage as OrganisationCompliancePolicies };